import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';
import { 
  Search, 
  Loader2,
  Mail,
  Phone,
  Building2,
  MapPin,
  Calendar,
  User,
  Briefcase,
  Heart,
  UserCircle,
  Clock,
  Star,
  Target,
  Linkedin,
  ExternalLink,
  Users,
  AlertCircle,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SharedDirectoryPage() {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ departmentId: '' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchSharedDirectory();
  }, [shareToken]);

  const fetchSharedDirectory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/public/shared-directory/${shareToken}`);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Ce lien de partage n'existe pas ou a été désactivé.");
      } else if (err.response?.status === 410) {
        setError("Ce lien de partage a expiré.");
      } else {
        setError("Une erreur est survenue lors du chargement du trombinoscope.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetail = async (employeeId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/api/public/shared-directory/${shareToken}/employee/${employeeId}`);
      setEmployeeDetail(response.data);
    } catch (err) {
      console.error('Error fetching employee detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeDetail(employee.id);
  };

  const closeDetailModal = () => {
    setSelectedEmployee(null);
    setEmployeeDetail(null);
  };

  // Fonction pour trier les employés par hiérarchie au sein de chaque département
  const sortByHierarchy = (employeesList) => {
    // Calculer le niveau hiérarchique basé sur le titre du poste
    const getPriorityFromTitle = (title) => {
      if (!title) return 99;
      const lowerTitle = title.toLowerCase();
      
      // Niveau 1: Directeurs (inclut directeur, directrice, DG, PDG, CEO, etc.)
      if (lowerTitle.includes('directeur') || lowerTitle.includes('directrice') || 
          lowerTitle.includes('dg') || lowerTitle.includes('pdg') || 
          lowerTitle.includes('ceo') || lowerTitle.includes('coo') || 
          lowerTitle.includes('cfo') || lowerTitle.includes('cto') ||
          lowerTitle.includes('président') || lowerTitle.includes('gérant')) return 1;
      
      // Niveau 2: Managers et Responsables
      if (lowerTitle.includes('manager') || lowerTitle.includes('responsable')) return 2;
      
      // Niveau 3: Chefs et Superviseurs
      if (lowerTitle.includes('chef') || lowerTitle.includes('superviseur') || 
          lowerTitle.includes('coordinateur') || lowerTitle.includes('head of')) return 3;
      
      // Niveau 4: Seniors et Leads
      if (lowerTitle.includes('senior') || lowerTitle.includes('principal') || 
          lowerTitle.includes('lead') || lowerTitle.includes('expert')) return 4;
      
      // Niveau 5: Assistants
      if (lowerTitle.includes('assistant') || lowerTitle.includes('adjoint')) return 5;
      
      // Niveau 6: Autres (opérateurs, techniciens, etc.)
      return 10;
    };
    
    return [...employeesList].sort((a, b) => {
      // 1. Trier par priorité du titre
      const priorityA = getPriorityFromTitle(a.jobTitle);
      const priorityB = getPriorityFromTitle(b.jobTitle);
      if (priorityA !== priorityB) return priorityA - priorityB;
      
      // 2. Trier par nom
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });
  };

  // Filtrer les employés
  const filteredEmployees = data?.employees?.filter(emp => {
    const matchesSearch = !search || 
      `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      (emp.jobTitle && emp.jobTitle.toLowerCase().includes(search.toLowerCase())) ||
      (emp.email && emp.email.toLowerCase().includes(search.toLowerCase()));
    
    const matchesDept = !filters.departmentId || 
      data.departments?.find(d => d.id === filters.departmentId)?.name === emp.department;
    
    return matchesSearch && matchesDept;
  }) || [];

  // Grouper par département pour l'affichage, puis trier chaque groupe
  const employeesByDepartment = filteredEmployees.reduce((acc, emp) => {
    const dept = emp.department || 'Sans département';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(emp);
    return acc;
  }, {});
  
  // Trier les employés au sein de chaque département
  Object.keys(employeesByDepartment).forEach(dept => {
    employeesByDepartment[dept] = sortByHierarchy(employeesByDepartment[dept]);
  });

  const getInitials = (firstName, lastName) => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const calculateSeniority = (hireDate) => {
    if (!hireDate) return null;
    const hire = new Date(hireDate);
    const now = new Date();
    const years = Math.floor((now - hire) / (365.25 * 24 * 60 * 60 * 1000));
    const months = Math.floor(((now - hire) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
    if (years > 0) return `${years} an${years > 1 ? 's' : ''} ${months > 0 ? `et ${months} mois` : ''}`;
    return `${months} mois`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Chargement du trombinoscope...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Accès non disponible</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const branding = {
    primaryColor: data?.company?.primaryColor || '#2054A6',
    accentColor: data?.company?.accentColor || '#F97316',
    logoUrl: data?.company?.logoUrl,
    companyName: data?.company?.name
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div 
        className="py-8 px-4"
        style={{ backgroundColor: branding.primaryColor }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            {branding.logoUrl ? (
              <img 
                src={branding.logoUrl} 
                alt={branding.companyName} 
                className="h-12 object-contain bg-white rounded px-3 py-1"
              />
            ) : (
              <div className="text-white text-2xl font-bold">
                {branding.companyName}
              </div>
            )}
            <div className="text-right text-white">
              <h1 className="text-2xl font-bold">{data?.shareInfo?.title || 'Trombinoscope'}</h1>
              {data?.shareInfo?.description && (
                <p className="text-white/80 text-sm mt-1">{data.shareInfo.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2 text-slate-600">
            <Users className="w-5 h-5" />
            <span className="font-medium">{data?.count || 0} collaborateur{(data?.count || 0) > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, poste..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="shared-directory-search"
                />
              </div>
              
              {data?.departments?.length > 0 && (
                <Select
                  value={filters.departmentId || 'all'}
                  onValueChange={(value) => setFilters({ departmentId: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Département" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les départements</SelectItem>
                    {data.departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employee Grid by Department */}
        {Object.keys(employeesByDepartment).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UserCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun collaborateur trouvé</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(employeesByDepartment).map(([deptName, employees]) => (
              <div key={deptName}>
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5" style={{ color: branding.primaryColor }} />
                  <h2 className="text-lg font-semibold text-slate-800">{deptName}</h2>
                  <Badge variant="secondary">{employees.length}</Badge>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {employees.map((emp) => (
                    <Card 
                      key={emp.id} 
                      className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                      onClick={() => handleEmployeeClick(emp)}
                      data-testid={`employee-card-${emp.id}`}
                    >
                      <div 
                        className="h-2" 
                        style={{ backgroundColor: branding.accentColor }}
                      />
                      <CardContent className="p-4 text-center">
                        <Avatar className="w-20 h-20 mx-auto mb-3 ring-2 ring-offset-2 group-hover:ring-4 transition-all" style={{ ringColor: branding.primaryColor }}>
                          {emp.photoUrl ? (
                            <AvatarImage src={emp.photoUrl} alt={`${emp.firstName} ${emp.lastName}`} />
                          ) : null}
                          <AvatarFallback 
                            className="text-lg font-semibold text-white"
                            style={{ backgroundColor: branding.primaryColor }}
                          >
                            {getInitials(emp.firstName, emp.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <h3 className="font-semibold text-slate-800 truncate">
                          {emp.firstName} {emp.lastName}
                        </h3>
                        <p className="text-sm text-slate-500 truncate">{emp.jobTitle || '-'}</p>
                        
                        {(emp.email || emp.phone) && (
                          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                            {emp.email && (
                              <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                                <Mail className="w-3 h-3" />
                                <span className="truncate">{emp.email}</span>
                              </div>
                            )}
                            {emp.phone && (
                              <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                                <Phone className="w-3 h-3" />
                                <span>{emp.phone}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-slate-400">
          <p>Trombinoscope partagé par {branding.companyName}</p>
        </div>
      </div>

      {/* Employee Detail Modal */}
      <Dialog open={!!selectedEmployee} onOpenChange={() => closeDetailModal()}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto p-0">
          {loadingDetail ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-slate-500">Chargement...</p>
            </div>
          ) : employeeDetail ? (
            <>
              {/* Header avec photo */}
              <div 
                className="relative p-6 text-white"
                style={{ backgroundColor: branding.primaryColor }}
              >
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="absolute top-2 right-2 text-white hover:bg-white/20"
                  onClick={closeDetailModal}
                >
                  <X className="w-5 h-5" />
                </Button>
                
                <div className="flex items-center gap-4">
                  <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                    {employeeDetail.photoUrl ? (
                      <AvatarImage src={employeeDetail.photoUrl} alt={`${employeeDetail.firstName} ${employeeDetail.lastName}`} />
                    ) : null}
                    <AvatarFallback className="text-2xl font-bold bg-white" style={{ color: branding.primaryColor }}>
                      {getInitials(employeeDetail.firstName, employeeDetail.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div>
                    <h2 className="text-2xl font-bold">
                      {employeeDetail.firstName} {employeeDetail.lastName}
                    </h2>
                    <p className="text-white/90 text-lg">{employeeDetail.jobTitle}</p>
                    {employeeDetail.department && (
                      <Badge className="mt-2 bg-white/20 text-white border-0">
                        {employeeDetail.department}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Contenu */}
              <div className="p-6 space-y-6">
                {/* Coordonnées */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {employeeDetail.email && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <a href={`mailto:${employeeDetail.email}`} className="text-sm font-medium text-blue-600 hover:underline">
                          {employeeDetail.email}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {employeeDetail.phone && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Téléphone</p>
                        <a href={`tel:${employeeDetail.phone}`} className="text-sm font-medium text-blue-600 hover:underline">
                          {employeeDetail.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {employeeDetail.site && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Site</p>
                        <p className="text-sm font-medium">{employeeDetail.site}</p>
                      </div>
                    </div>
                  )}
                  
                  {employeeDetail.hireDate && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Ancienneté</p>
                        <p className="text-sm font-medium">{calculateSeniority(employeeDetail.hireDate)}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Bio */}
                {employeeDetail.bio && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" /> À propos
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{employeeDetail.bio}</p>
                  </div>
                )}
                
                {/* Missions */}
                {employeeDetail.missions && employeeDetail.missions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" /> Missions principales
                    </h3>
                    <ul className="space-y-2">
                      {employeeDetail.missions.map((mission, idx) => (
                        <li key={`mission-${idx}-${mission}`} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: branding.accentColor }} />
                          {mission}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Valeurs clés */}
                {employeeDetail.valeursCles && employeeDetail.valeursCles.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Heart className="w-4 h-4" /> Valeurs clés
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {employeeDetail.valeursCles.map((valeur, idx) => (
                        <Badge 
                          key={`valeur-${idx}-${valeur}`} 
                          variant="outline" 
                          className="border-2"
                          style={{ borderColor: branding.accentColor, color: branding.primaryColor }}
                        >
                          {valeur}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* LinkedIn */}
                {employeeDetail.linkedinUrl && (
                  <div className="pt-4 border-t">
                    <a 
                      href={employeeDetail.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077b5] text-white rounded-lg hover:bg-[#006699] transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                      Voir le profil LinkedIn
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
